import {
    Box,
    Grid,
    IconButton,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import React, { useRef } from "react";
import { MFTextField } from "../../../../lib/formik";
import { TextDatePicker } from "../../../onboarding/DatePickerWithTextFeild";
import MFSelectField from "../../../../lib/formik/SelectField";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import {
    FormikContextType,
    useFormikContext
} from '../../../../lib/formik/Formik';
import {
    allowDecimalNumbers,
    formatCurrency,
    preventSpecialCharacters,
} from "../../../../utils/utilityFunctions";
import dayjs from "../../../../utils/dayjs";
import { CurrencyConversionToWords } from "../commonComponents";
import FormikSearchDropdown from "../../../../lib/formik/FormikSearchDropdown";
import * as yup from 'yup';

export default function PmsForm({
    securitiesData,
    fetchSecuritiesData,
    setValidations,
    isLoading
}: {
    securitiesData: Record<string, any>;
    fetchSecuritiesData: (searchInput: string) => void;
    setValidations: (values: Record<string, any>) => void;
    isLoading: boolean;
}): JSX.Element {
    const { currencySymbol, locale = "en-IN", currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);
    const { businessDate = "" } = useSelector((store: RootStateType) => store.navbarInfo);

    const {
        values,
        setFieldValue,
        resetForm
    }: FormikContextType<Record<string, any>> = useFormikContext();

    // Timer ref for handleSecurityChange — ensures only the latest selection's resetForm fires
    const securityChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleDateChange = (fieldName: string, selectedDate: any) => {
        setFieldValue(fieldName, selectedDate?.isValid() ? selectedDate : "", true)
    }

    const handleAmountPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")

        const modifiedText = `${clipboardData ?? ""}`?.replaceAll(",", "")

        setFieldValue("amount", modifiedText);
    }

    const handleSecurityChange = (selectedObj: Record<string, any>) => {
        // For Purchase: populate calculations from security (old flow — no folio involved)
        // For STP Purchase/Withdrawal: only set security+issuer, calculations happen in handleFolioChange
        //
        // WHY setTimeout(50): FormikSearchDropdown's internal onChange fires before Formik has
        // committed the newly selected value into its state. Calling resetForm synchronously here
        // would spread a stale values snapshot that still holds the previous security. The 50ms
        // defer lets Formik finish its own state update cycle before we reset dependent fields.
        //
        // WHY valuesSnapshot + debounce: Even though we defer execution, we still need the form
        // state at the moment the user made their selection (e.g. transactionType, account, client
        // details). Capturing values synchronously into valuesSnapshot before the timeout ensures
        // the spread inside resetForm reflects that moment. The timer is also cleared on each new
        // call (debounce) so only the latest selection's resetForm fires if the user changes
        // security rapidly within the 50ms window.
        const valuesSnapshot = values
        if (securityChangeTimerRef.current !== null) {
            clearTimeout(securityChangeTimerRef.current)
        }
        securityChangeTimerRef.current = setTimeout(() => {
            securityChangeTimerRef.current = null
            const isPurchase = valuesSnapshot?.transactionType === "Purchase"
            const marketPrice = !Number.isNaN(Number(selectedObj?.marketPrice ?? 0)) ? Number(selectedObj?.marketPrice ?? 0) : 0
            const qtySaleable = !Number.isNaN(Number(selectedObj?.qtySaleable ?? 0)) ? Number(selectedObj?.qtySaleable ?? 0) : 0
            const calculatedPortfolioValue = (marketPrice * qtySaleable)?.toLocaleString(locale)

            resetForm({
                values: {
                    ...valuesSnapshot,
                    security: selectedObj ?? null,
                    folioNumber: "",
                    selectedFolioData: null,
                    issuer: selectedObj?.issuerName ?? "",
                    currentPortfolioValue: isPurchase && selectedObj ? calculatedPortfolioValue : "",
                    lastValuationDate: isPurchase && selectedObj && dayjs(selectedObj?.marketPriceDate).isValid()
                        ? dayjs(selectedObj?.marketPriceDate, "YYYYMMDD")
                        : "",
                    amount: "",
                    settlementBankNumber: isPurchase && selectedObj && selectedObj?.bankAccNo
                        ? {
                            ...(selectedObj ?? {}),
                            bankCode: selectedObj?.bankCode ?? "",
                            bankAccCcy: selectedObj?.bankAccCcy ?? "",
                            bankAccType: selectedObj?.bankAccType ?? "",
                            bankAccNo: selectedObj?.bankAccNo ?? "",
                            bankAccCode: selectedObj?.bankAccCode ?? "",
                            bankName: selectedObj?.bankName ?? "",
                            label: `${selectedObj?.bankAccNo ?? ""} ${selectedObj?.bankName ?? ""}`,
                            value: selectedObj?.bankAccNo ?? ""
                        }
                        : null,
                    settlementBank: isPurchase && selectedObj?.bankAccNo ? selectedObj?.bankName ?? "" : "",
                    settlementBankIfsc: isPurchase && selectedObj?.bankAccNo ? selectedObj?.ifscCode ?? "" : ""
                }
            })
        }, 50)
    }

    const handleFolioChange = (selectedFolioNo: string) => {
        // Find the matching folio object from the selected security's folioNoList
        const folioNoList: Record<string, any>[] = values?.security?.folioNoList ?? []
        const folioObj = folioNoList.find((obj) => obj?.folioNo === selectedFolioNo) ?? null

        if (!folioObj) {
            resetForm({
                values: {
                    ...values,
                    folioNumber: selectedFolioNo,
                    selectedFolioData: null,
                    currentPortfolioValue: "",
                    lastValuationDate: "",
                    settlementBankNumber: null,
                    settlementBank: "",
                    settlementBankIfsc: ""
                }
            })
            return
        }

        const marketPrice = !Number.isNaN(Number(folioObj?.marketPrice ?? 0)) ? Number(folioObj?.marketPrice ?? 0) : 0
        const qtySaleable = !Number.isNaN(Number(folioObj?.qtySaleable ?? 0)) ? Number(folioObj?.qtySaleable ?? 0) : 0
        const calculatedPortfolioValue = (marketPrice * qtySaleable)?.toLocaleString(locale)

        // NOTE (CORR-32): both Withdrawal and non-Withdrawal use identical amount validation —
        // the previously commented-out max-amount cap was the only difference. Collapsed to one call.
        setValidations((prevState: Record<string, any>) => ({
            ...prevState,
            amount: yup
                .string()
                .transform((value) => `${value ?? ""}`?.replaceAll(',', ''))
                .required('Amount is required')
                .test('is-number', 'Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) })
                .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                    return Number(value) > 0
                })
        }))

        resetForm({
            values: {
                ...values,
                folioNumber: selectedFolioNo,
                selectedFolioData: folioObj,
                currentPortfolioValue: calculatedPortfolioValue,
                lastValuationDate: dayjs(folioObj?.marketPriceDate).isValid() ? dayjs(folioObj?.marketPriceDate, "YYYYMMDD") : "",
                amount: "",
                settlementBankNumber: {
                    ...(folioObj ?? {}),
                    bankCode: folioObj?.bankCode ?? "",
                    bankAccCcy: folioObj?.bankAccCcy ?? "",
                    bankAccType: folioObj?.bankAccType ?? "",
                    bankAccNo: folioObj?.bankAccNo ?? "",
                    bankAccCode: folioObj?.bankAccCode ?? "",
                    bankName: folioObj?.bankName ?? "",
                    label: `${folioObj?.bankAccNo ?? ""} ${folioObj?.bankName ?? ""}`,
                    value: folioObj?.bankAccNo ?? ""
                },
                settlementBank: folioObj?.bankName ?? "",
                settlementBankIfsc: folioObj?.ifscCode ?? ""
            }
        })
    }

    const handleTransactionTypeChange = async (event: SelectChangeEvent<unknown>) => {
        resetForm({
            values: {
                ...values,
                transactionType: event.target.value,
                folioNumber: "",
                selectedFolioData: null,
                security: null,
                issuer: "",
                amount: "",
                currentPortfolioValue: "",
                lastValuationDate: "",
                settlementBankNumber: null,
                settlementBank: "",
                settlementBankIfsc: "",
                settlementMethod: ""
            }
        })
    }

    // NOTE (CORR-03): items were previously hardcoded to []. Now populated from
    // values.security.folioNoList. The reviewer's diff was stale.
    const refactoredAmount = `${values?.amount ?? ""}`?.replaceAll(',', '')
    const refactoredCurrentPortfolioValue = `${values?.currentPortfolioValue ?? ""}`?.replaceAll(',', '')

    return (
        <Grid
            container
            columnSpacing={3}
        >
            {/* <Grid item xs={12} lg={4}>
                <MFTextField
                    name="orderNumber"
                    label="Order Number"
                    placeholder=""
                />
            </Grid> */}
            <Grid item xs={12} lg={4}>
                <TextDatePicker
                    name="orderDate"
                    label="Order Date *"
                    placeholder=""
                    maxDate={
                        businessDate ?
                            dayjs(businessDate, "DD/MM/YYYY").toDate() :
                            new Date()
                    }
                    customHandleChange={(selectedDate: any) => handleDateChange('orderDate', selectedDate)}
                    disabled
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="transactionType"
                    label="Transaction Type *"
                    items={[
                        { name: "Purchase" },
                        { name: "Withdrawal" } //call holding api
                    ].map((obj) => ({
                        key: obj.name,
                        value: obj.name,
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleTransactionTypeChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="security"
                    label="Security *"
                    options={securitiesData?.securities?.map((obj: Record<string, any>) => ({
                        ...obj,
                        label: `${obj?.securityName ?? ""}${obj?.isin ? ` (${obj.isin})` : ""}`,
                        value: obj?.security ?? ""
                    })) ?? []}
                    fetchData={fetchSecuritiesData}
                    disabled={isLoading || !values?.transactionType}
                    onChange={handleSecurityChange}
                    filterOptions={(x) => x}
                />
            </Grid>
            {(["Purchase"].includes(values.transactionType)) && (
                <Grid item xs={12} lg={4}>
                    <Box sx={{ position: "relative" }}>
                        <MFTextField
                            name="folioNumber"
                            label="Folio Number"
                            placeholder=""
                            onKeyDown={preventSpecialCharacters}
                            disabled={isLoading || !values?.security}
                            value={values?.folioNumber ?? ""}
                        />
                        {values?.folioNumber && !isLoading && (
                            <IconButton
                                size="small"
                                onClick={() => setFieldValue("folioNumber", "")}
                                sx={{
                                    position: "absolute",
                                    right: "8px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    padding: "2px"
                                }}
                                aria-label="Clear folio number"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            )}
            {(["STP Purchase", "Withdrawal"].includes(values.transactionType)) && (
                <Grid item xs={12} lg={4}>
                    <Box sx={{ position: "relative" }}>
                        <MFSelectField
                            name="folioNumber"
                            label="Folio Number *"
                            items={(values?.security?.folioNoList ?? [])
                                .filter((obj: Record<string, any>) => obj?.folioNo)
                                .map((obj: Record<string, any>) => ({
                                    key: obj.folioNo,
                                    value: obj.folioNo,
                                }))}
                            disabled={isLoading || !values?.security}
                            value={values?.folioNumber ?? ""}
                            onChange={(e) => handleFolioChange(e.target.value as string)}
                        />
                        {values?.folioNumber && !isLoading && (
                            <IconButton
                                size="small"
                                onClick={() => resetForm({
                                    values: {
                                        ...values,
                                        folioNumber: "",
                                        selectedFolioData: null,
                                        currentPortfolioValue: "",
                                        lastValuationDate: "",
                                        amount: "",
                                        settlementBankNumber: null,
                                        settlementBank: "",
                                        settlementBankIfsc: ""
                                    }
                                })}
                                sx={{
                                    position: "absolute",
                                    right: "36px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    padding: "2px",
                                    zIndex: 1
                                }}
                                aria-label="Clear folio number"
                            >
                                <ClearIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            )}
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="issuer"
                    label="Issuer"
                    placeholder=""
                    disabled={true}
                    value={values?.issuer ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="amount"
                    label="Amount *"
                    placeholder=""
                    startAdornment={values?.amount ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({
                                color: (isLoading || !values?.security) ? theme.palette.text.secondary : theme.palette.text.primary
                            })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={isLoading || !values?.security || (["STP Purchase", "Withdrawal"].includes(values?.transactionType) && !values?.folioNumber)}
                    onKeyDown={allowDecimalNumbers}
                    formatCurrency
                    formatCurrencyLocaleOptions={{
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 4
                    }}
                    onPaste={handleAmountPaste}
                    value={values?.amount ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAmount} />
            </Grid>
            {values?.transactionType === "Withdrawal" && (
                <>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="currentPortfolioValue"
                            label="Current Portfolio Value"
                            placeholder=""
                            startAdornment={values?.currentPortfolioValue ? (
                                <Typography
                                    variant='body_500'
                                    sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                                >
                                    {String.fromCharCode(parseInt(currencySymbol, 16))}
                                </Typography>
                            ) : undefined}
                            sx={{
                                '&.MuiOutlinedInput-root': {
                                    "& fieldset>legend": {
                                        fontSize: "0.78em",
                                    }
                                }
                            }}
                            disabled={true}
                            formatCurrency
                            value={values?.currentPortfolioValue ?? ""}
                            onKeyDown={allowDecimalNumbers}
                        />
                        <CurrencyConversionToWords value={refactoredCurrentPortfolioValue} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <TextDatePicker
                            name="lastValuationDate"
                            label="Last Valuation Date"
                            placeholder=""
                            disabled={true}
                            maxDate={new Date("2099-12-31")}
                            customHandleChange={(selectedDate: any) => handleDateChange('lastValuationDate', selectedDate)}
                        />
                    </Grid>
                </>
            )}
            <Grid item xs={12}>
                <MFTextField
                    name="remarks"
                    label="Remarks *"
                    placeholder=""
                    multiline
                    inputProps={{ maxLength: 200 }}
                    value={values?.remarks ?? ""}
                    disabled={isLoading}
                />
                <Typography
                    variant="overline_400"
                    sx={(theme: Theme) => ({
                        textAlign: "end",
                        color: theme.palette.text.secondary,
                        marginTop: "-10px"
                    })}
                >
                    Max 200 characters
                </Typography>
            </Grid>
        </Grid>
    )
}